import { Pipe, PipeTransform } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  selectFilePrices,
  selectFilesInBasket,
  selectPackagesInBasket,
  selectPhotoGroups,
  selectPhotosList,
  selectPriceToAddToEveryFileInProduct,
  selectPriceToAddToFirstPrint,
  selectPrintsInBasket,
  selectProductsInBasket,
  selectRetouchLevels,
} from '../../actions/selector';
import {
  listFilePriceEntitlements,
  ShoppingCartState,
} from '../../actions/standalone';
import { map } from 'rxjs/operators';
import { singleStepPriceCalculator } from '../../../../step-pricing/StepPriceCalculator';
import { combineLatest } from 'rxjs';
import { StepPricingMode } from '../../../../../../../../photographer-panel/src/graphql';
import { AvailableCurrenciesService } from 'ZalamoLibrary/src/lib/zalamo/currencies/available-currencies.service';

@Pipe({
    name: 'filePrice',
    standalone: false
})
export class FilePricePipe implements PipeTransform {
  constructor(
    private availableCurrencies: AvailableCurrenciesService,
    private store: Store<{ shoppingCart: ShoppingCartState }>,
  ) {}

  transform(
    filePricingId: string,
    timeAddedToShoppingCart: number,
    index: number,
    free: boolean,
  ) {
    return combineLatest([
      combineLatest([
        this.store.pipe(select(selectRetouchLevels)),
        this.store.pipe(select(selectProductsInBasket)),
        this.store.pipe(select(selectPriceToAddToEveryFileInProduct)),
        this.store.pipe(select(selectPhotosList)),
        this.store.pipe(select(selectPhotoGroups)),
        combineLatest([
          this.store.pipe(select(selectFilesInBasket)),
          this.store.pipe(select(selectFilePrices)),
          this.store.pipe(select(selectPriceToAddToFirstPrint)),
          this.store.pipe(select(selectPrintsInBasket)),
          this.store.pipe(select(selectPackagesInBasket)),
        ]),
      ]).pipe(
        map(
          ([
            retouchLevels,
            productsInBasket,
            priceToAddToEveryFileInProduct,
            photosList,
            photoGroups,
            [
              filesInBasket,
              filePrices,
              priceToAddToFirstPrint,
              printsInBasket,
              packagesInBasket,
            ],
          ]) => {
            const fileInBasket = filesInBasket.find(
              (x, i) =>
                x.timeAddedToShoppingCart === timeAddedToShoppingCart &&
                i === index,
            );
            const standardPrice = filePrices.find(
              (file) => file.filePricingId === filePricingId,
            );
            const photoGroupId = photosList
              .find(({ photos }) =>
                photos.some(({ photoId }) => photoId === fileInBasket?.photoId),
              )
              ?.photos.find(
                ({ photoId }) => photoId === fileInBasket?.photoId,
              )?.photoGroupId;
            const config = photoGroups.find(
              (group) => group.photoGroupId === photoGroupId,
            )?.config;
            const groupPrice =
              config?.individualSellSettings &&
              config.sellFiles &&
              config.customFilesPricing
                ? config.files.find(
                    (file) => file.filePricingId === filePricingId,
                  )
                : undefined;
            const pricingList = listFilePriceEntitlements({
              photoGroups,
              photoGroupList: photosList,
              priceToAddToFirstPrint,
              priceToAddToEveryFileInProduct,
              filePrices,
              printsInBasket,
              filesInBasket,
              productsInBasket,
              packagesInBasket,
            }).filter(
              (entitlement) => entitlement.filePricingId === filePricingId,
            );
            const entitlementIndex = pricingList.findIndex(
              (entitlement) =>
                entitlement.photoId === fileInBasket?.photoId &&
                entitlement.type === 'file' &&
                entitlement.timeAddedToShoppingCart ===
                  (fileInBasket?.timeAddedToShoppingCart ?? 0),
            );
            return {
              countAboveThisFile:
                entitlementIndex < 0 ? -1 : entitlementIndex + 1,
              allCount: pricingList.length,
              retouchPrice:
                retouchLevels?.find(
                  ({ retouchLevelId }) =>
                    retouchLevelId === fileInBasket?.retouchLevelId,
                )?.price || [],
              fromOldBasket: !!fileInBasket?.fromOldBasket,
              pricing: groupPrice ? groupPrice : standardPrice,
            };
          },
        ),
      ),
    ]).pipe(
      map(
        ([
          {
            countAboveThisFile,
            allCount,
            retouchPrice,
            fromOldBasket,
            pricing,
          },
        ]) => {
          if (countAboveThisFile === -1) {
            return null;
          }
          const codeInSession = this.availableCurrencies.getCurrentCurrency();
          let filePrice = pricing?.stepPricingEnabled
            ? singleStepPriceCalculator(
                countAboveThisFile,
                allCount,
                pricing.stepPricing,
                pricing.price,
              )
            : pricing?.price;
          const earlierFile =
            pricing?.stepPricingEnabled &&
            pricing?.stepPricing.mode === StepPricingMode.Bundles
              ? singleStepPriceCalculator(
                  countAboveThisFile - 1,
                  allCount,
                  pricing.stepPricing,
                  pricing.price,
                )
              : [{ price: 0, code: codeInSession }];
          if (!pricing?.stepPricing.isFirstStepFree && !earlierFile?.length) {
            filePrice = [];
          }
          const inPackage =
            (((pricing?.stepPricing.isFirstStepFree &&
              countAboveThisFile <= pricing?.stepPricing.firstStep) ||
              !pricing?.stepPricing.isFirstStepFree) &&
              !earlierFile?.length &&
              pricing?.stepPricingEnabled &&
              pricing?.stepPricing.mode === StepPricingMode.Bundles) ||
            (pricing?.stepPricingEnabled &&
              pricing?.stepPricing.isFirstStepFree &&
              countAboveThisFile <= pricing.stepPricing.firstStep &&
              (pricing?.stepPricing.mode === StepPricingMode.ForOverTreshold ||
                pricing?.stepPricing.mode === StepPricingMode.ForAll));
          return {
            inPackage,
            price: !!filePrice?.length
              ? filePrice.map(({ price, code }) => ({
                  price:
                    (free || fromOldBasket ? 0 : price) +
                    (retouchPrice.find((r) => r.code === code)
                      ? retouchPrice.find((r) => r.code === code).price
                      : 0),
                  code,
                }))
              : inPackage
              ? retouchPrice
              : [{ price: 0, codeInSession }],
          };
        },
      ),
    );
  }
}
